import { ApolloQueryResult } from '@apollo/client';
import {
    Button,
    CancelIcon,
    ContentCopyIcon,
    ContentCopyOutlinedIcon,
    DeleteOutlinedIcon,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    Stack,
    TextField,
} from '@ds/coolshop';
import { ColIds } from '@ds/data-table';
import {
    useCreateObjectSchemaMutation,
    useDeleteObjectSchemaMutation,
    useDuplicateObjectSchemaMutation,
    useUpdateObjectSchemaMutation,
} from '@modules/product';
import { FormElement } from '@modules/ui';
import {
    AssignmentPropagationDirection,
    AssignmentPropagationInput,
    AssignmentPropagationMode,
    LinkedNodeDeleteStrategy,
    NodeReadonlyRuleInput,
    ObjectSchema,
} from '@src/__generated__/types';
import { HeaderBarIconButton } from '@src/_new/modules/edit-node-header-bar';
import {
    isChanged,
    ReadFilterDraft,
    ReadFiltersFields,
    toDrafts,
    toReadFilterInputs,
    useReadFiltersAvailable,
    useSetObjectSchemaReadFiltersMutation,
} from '@src/_new/modules/read-filters';
// mutations and types
import { ModeType, ProductData } from '@src/assets/types';
import {
    ControlFieldElement,
    SelectFieldElement,
    TextFieldElement,
} from '@src/components/formFields';
import { useUpdateListNameMutation } from '@src/components/hierarchies/Hierarchy.generated';
import { useDirtyContext } from '@src/components/hierarchies/shared/DirtyContext';
import SubmitButton from '@src/components/shared/buttons/SubmitButton';
import { ConfirmDialog, ConfirmDialogResponse } from '@src/components/shared/ConfirmDialog';
import SnackbarUtils from '@src/components/shared/SnackBarUtils';
// contexts
import { useMemo, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { useProductTreeCtx } from '../../../../contexts/ProductTreeCtx/ProductTreeCtx';
import { ProductActionBar } from '../../../ProductActionBar/ProductActionBar';
import { ProductFormTitle } from '../../../ProductActionBar/ProductFormTitle';
import { InlineToggle } from '../components/InlineToggle';
import { SectionDivider } from '../components/SectionDivider';

export const VIEW = {
    PRODUCT_CREATED: 'PRODUCT_INFO',
    GROUP_CREATED: 'CONTENT_TYPE_GROUP',
    CONTENT_TYPE_CREATED: 'CONTENT_TYPE',
};

export interface ISetCreationState {
    onCreation: (view: { VIEW_TYPE: string; VIEW_ID: string }) => void;
    refetchProducts?: () => Promise<ApolloQueryResult<ProductData>>;
}

type NewProductInfoProps = {
    mode?: ModeType;
    onCreation?: ISetCreationState['onCreation'];
    refetchProducts?: () => Promise<ApolloQueryResult<ProductData>>;
};

type OnFormSubmitArgs = {
    code: string;
    disableDeleteHierarchy: boolean;
    disableDelete: boolean;
    disableEditCode: boolean;
    listName: string;
    codeLabel: string;
    showListNameInEdit: boolean;
    automaticParentCreation: boolean;
    codeCreationExpression: string;
    enablePublish: boolean;
    bulkPublish: boolean;
    showPublishInfo: boolean;
    enableOwner: boolean;
    hideDisableToggle: boolean;
    /** Comma-separated node depths as typed in the form; empty = every depth. */
    hideDisableToggleDepths: string;
    dataTable: boolean;
    hideChangeParent: boolean;
    linkedNodeDeleteStrategy: LinkedNodeDeleteStrategy;
    /** JSONata of the node readonly rule; empty = no rule. */
    nodeReadonlyExpression: string;
    /** Comma-separated "group.field" entries still editable on a locked node, as typed. */
    nodeReadonlyExcludedFields: string;
    nodeReadonlyMessage: string;
    assignmentPropagationDirection: AssignmentPropagationDirection;
    assignmentPropagationMode: AssignmentPropagationMode;
    /** Comma-separated object schema codes the propagation is limited to, as typed; empty = all. */
    assignmentPropagationSchemaCodes: string;
    /** Only shown and saved where useReadFiltersAvailable is true. */
    readFilters: ReadFilterDraft[];
};

// The option ids are selected dynamically, so they are declared with defineMessages for the
// formatjs extractor to find them.
const linkedNodeDeleteStrategyMessages = defineMessages({
    [LinkedNodeDeleteStrategy.Ask]: {
        id: 'linked-node-delete-strategy.ask',
        defaultMessage: 'Chiedi',
    },
    [LinkedNodeDeleteStrategy.Cascade]: {
        id: 'linked-node-delete-strategy.cascade',
        defaultMessage: 'Elimina i nodi collegati',
    },
    [LinkedNodeDeleteStrategy.Unlink]: {
        id: 'linked-node-delete-strategy.unlink',
        defaultMessage: 'Rimuovi i collegamenti',
    },
});

const assignmentPropagationDirectionMessages = defineMessages({
    [AssignmentPropagationDirection.None]: {
        id: 'assignment-propagation-direction.none',
        defaultMessage: 'Nessuna propagazione',
    },
    [AssignmentPropagationDirection.LinkedBy]: {
        id: 'assignment-propagation-direction.linked-by',
        defaultMessage: 'Nodi che collegano questo nodo',
    },
    [AssignmentPropagationDirection.Linked]: {
        id: 'assignment-propagation-direction.linked',
        defaultMessage: 'Nodi collegati da questo nodo',
    },
    [AssignmentPropagationDirection.Both]: {
        id: 'assignment-propagation-direction.both',
        defaultMessage: 'Entrambi',
    },
});

const assignmentPropagationModeMessages = defineMessages({
    [AssignmentPropagationMode.Auto]: {
        id: 'assignment-propagation-mode.auto',
        defaultMessage: 'Automatica',
    },
    [AssignmentPropagationMode.Ask]: {
        id: 'assignment-propagation-mode.ask',
        defaultMessage: 'Chiedi',
    },
});

// "1, 3,4" -> [1, 3, 4]; anything that is not a positive integer is dropped.
const parseDepths = (value: string): number[] =>
    value
        .split(',')
        .map(part => Number.parseInt(part.trim(), 10))
        .filter(depth => Number.isInteger(depth) && depth > 0);

// The three rule fields of the form -> the mutation input. A blank expression means the schema
// has no rule, so null is sent (the mutation stores whatever it receives, an omission included).
const buildNodeReadonlyRule = (values: OnFormSubmitArgs): NodeReadonlyRuleInput | null => {
    const expression = values.nodeReadonlyExpression.trim();
    if (!expression) {
        return null;
    }
    const excludedFields = values.nodeReadonlyExcludedFields
        .split(',')
        .map(field => field.trim())
        .filter(Boolean);
    const message = values.nodeReadonlyMessage.trim();
    return {
        expression,
        excludedFields: excludedFields.length ? excludedFields : null,
        message: message || null,
    };
};

// The three propagation fields of the form -> the mutation input. A `none` direction means the
// schema does not propagate, so null is sent (an update that omits it clears it as well).
const buildAssignmentPropagation = (
    values: OnFormSubmitArgs,
): AssignmentPropagationInput | null => {
    if (values.assignmentPropagationDirection === AssignmentPropagationDirection.None) {
        return null;
    }
    const objectSchemaCodes = values.assignmentPropagationSchemaCodes
        .split(',')
        .map(code => code.trim())
        .filter(Boolean);
    return {
        direction: values.assignmentPropagationDirection,
        mode: values.assignmentPropagationMode,
        objectSchemaCodes: objectSchemaCodes.length ? objectSchemaCodes : null,
    };
};

function extractListNames(product?: ObjectSchema) {
    const list = product?.contentTypeGroups?.flatMap(ctg =>
        ctg.contentTypes?.map(ct => ({ ctg: ctg.code, ...ct })),
    );
    return [
        `${ColIds.UNGROUPED_CODE}`,
        ...(list
            ?.filter(ct => !ct?.multiValue && (ct?.type === 'text' || ct?.type === 'select'))
            .map(ct => `${ct?.ctg}-${ct?.code}`) ?? []),
    ];
}

export const NewProductInfo = ({
    mode,
    onCreation,
    refetchProducts,
}: NewProductInfoProps): JSX.Element => {
    const intl = useIntl();
    const { setIsDirty } = useDirtyContext();

    const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false);
    const [newProductInfoCode, setNewProductInfoCode] = useState<string>();
    /**
     * handle delete productInfo dialog state
     */
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    /**
     * state for tracking the selected productInfo to be deleted
     */
    const [selectedProductInfo, setSelectedProductInfo] = useState<ObjectSchema | null>(null);

    const [newProductInfo, { loading: isCreatingProductInfo }] = useCreateObjectSchemaMutation();
    const [updateProductInfo, { loading: isUpdatingProductInfo }] = useUpdateObjectSchemaMutation();
    const [removeProductInfo, { loading: isRemovingProductInfo }] = useDeleteObjectSchemaMutation();
    const [duplicateObjectSchema, { loading: isDuplicatingObjectSchema }] =
        useDuplicateObjectSchemaMutation();
    const [updateListName] = useUpdateListNameMutation();
    const [setReadFilters] = useSetObjectSchemaReadFiltersMutation();
    const readFiltersAvailable = useReadFiltersAvailable();

    /**
     * Close dialog. If a product is selected
     *  1-delete it by id
     *  2-refetch all products
     *  3-reset status for the selected product
     * @param response confirm dialog confirmation
     */
    const handleDeleteClose = async (response: ConfirmDialogResponse) => {
        setDeleteDialogOpen(false);
        if (response === ConfirmDialogResponse.OK && selectedProductInfo) {
            // remove productInfo
            await removeProductInfo({
                variables: {
                    aggregateId: selectedProductInfo.id,
                },
            });
            // refresh products
            await refetchProducts?.();
            // if you are here mutations are fulfilled
            SnackbarUtils.success(`Product ${selectedProductInfo.code} successfully removed.`);
            // reset state
            setSelectedProductInfo(null);
        }
    };

    /*
     * retrieve product as {code: "Product", contentTypeGroups[], everyNode[], hierarchies[], __typename: "ProductInfo", _id}
     */
    const {
        state: { product },
    } = useProductTreeCtx();

    const onFormSubmit = async (values: OnFormSubmitArgs) => {
        // Saved first, by their own command: the server may reject an expression (allowlist,
        // EXPLAIN), and then nothing is saved and the form stays dirty for a retry.
        if (
            mode === ModeType.EDIT_PRODUCT_INFO &&
            readFiltersAvailable &&
            isChanged(values.readFilters, product.readFilters ?? [])
        ) {
            try {
                await setReadFilters({
                    variables: {
                        objectSchemaId: product.id,
                        readFilters: toReadFilterInputs(values.readFilters),
                    },
                });
            } catch (err) {
                // The server's message names the rejected construct (INVALID_READ_FILTER: …).
                SnackbarUtils.error(err instanceof Error ? err.message : String(err), {
                    persist: true,
                });
                return false;
            }
        }
        if (mode === ModeType.EDIT_PRODUCT_INFO) {
            await updateProductInfo({
                variables: {
                    aggregateId: product.id,
                    code: values.code,
                    disableDeleteHierarchy: !!values.disableDeleteHierarchy,
                    disableDelete: !!values.disableDelete,
                    disableEditCode: !!values.disableEditCode,
                    listName: values.listName,
                    codeLabel: values.codeLabel,
                    showListNameInEdit: values.showListNameInEdit,
                    codeCreationExpression: values.codeCreationExpression,
                    automaticParentCreation: values.automaticParentCreation,
                    enablePublish: values.enablePublish,
                    bulkPublish: values.bulkPublish,
                    showPublishInfo: values.showPublishInfo,
                    enableOwner: values.enableOwner,
                    hideDisableToggle: values.hideDisableToggle,
                    hideDisableToggleDepths: values.hideDisableToggle
                        ? parseDepths(values.hideDisableToggleDepths)
                        : [],
                    dataTable: values.dataTable,
                    hideChangeParent: values.hideChangeParent,
                    linkedNodeDeleteStrategy: values.linkedNodeDeleteStrategy,
                    // An update that omits the rule clears it: always send the form's value.
                    nodeReadonly: buildNodeReadonlyRule(values),
                    assignmentPropagation: buildAssignmentPropagation(values),
                },
            });
            SnackbarUtils.success(`Product ${values.code} successfully updated.`);
        } else {
            await newProductInfo({
                variables: {
                    code: values.code,
                    disableDeleteHierarchy: !!values.disableDeleteHierarchy,
                    disableDelete: !!values.disableDelete,
                    disableEditCode: !!values.disableEditCode,
                    listName: values.listName,
                    codeLabel: values.codeLabel,
                    showListNameInEdit: values.showListNameInEdit,
                    codeCreationExpression: values.codeCreationExpression,
                    automaticParentCreation: values.automaticParentCreation,
                    enablePublish: values.enablePublish,
                    bulkPublish: values.bulkPublish,
                    showPublishInfo: values.showPublishInfo,
                    enableOwner: values.enableOwner,
                    hideDisableToggle: values.hideDisableToggle,
                    hideDisableToggleDepths: values.hideDisableToggle
                        ? parseDepths(values.hideDisableToggleDepths)
                        : [],
                    dataTable: values.dataTable,
                    hideChangeParent: values.hideChangeParent,
                    linkedNodeDeleteStrategy: values.linkedNodeDeleteStrategy,
                    nodeReadonly: buildNodeReadonlyRule(values),
                    assignmentPropagation: buildAssignmentPropagation(values),
                },
            });
            SnackbarUtils.success(`Product ${values.code} successfully created.`);
        }

        // Update list name if needed (edit only: a new product has no previous list name, and
        // the selected product in the sidebar is not the one being created)
        if (mode === ModeType.EDIT_PRODUCT_INFO && product?.listName !== values.listName) {
            await updateListName({
                variables: {
                    objectSchema: product.id,
                    listName: values.listName,
                },
            });
            SnackbarUtils.success(`List name successfully updated.`);
        }

        await refetchProducts?.();

        if (onCreation) onCreation({ VIEW_TYPE: VIEW.PRODUCT_CREATED, VIEW_ID: values.code });
        setIsDirty(false);
        return true;
    };

    const deleteHandler = () => {
        setSelectedProductInfo(product);
        setDeleteDialogOpen(true);
    };

    // The selected product only seeds the form in edit mode: a new product starts blank even
    // while another one is still selected in the sidebar.
    const editedProduct = mode === ModeType.EDIT_PRODUCT_INFO ? product : undefined;

    const defaultValues = useMemo(
        () => ({
            code: editedProduct?.code ?? '',
            disableDeleteHierarchy: !!editedProduct?.disableDeleteHierarchy,
            disableDelete: !!editedProduct?.disableDelete,
            disableEditCode: !!editedProduct?.disableEditCode,
            listName: editedProduct?.listName ?? ColIds.UNGROUPED_CODE,
            codeLabel: editedProduct?.codeLabel ?? '',
            showListNameInEdit: !!editedProduct?.showListNameInEdit,
            codeCreationExpression: editedProduct?.codeCreationExpression ?? '',
            automaticParentCreation: !!editedProduct?.automaticParentCreation,
            enablePublish: !!editedProduct?.enablePublish,
            bulkPublish: !!editedProduct?.bulkPublish,
            showPublishInfo: !!editedProduct?.showPublishInfo,
            enableOwner: !!editedProduct?.enableOwner,
            hideDisableToggle: !!editedProduct?.hideDisableToggle,
            hideDisableToggleDepths: (editedProduct?.hideDisableToggleDepths ?? []).join(', '),
            dataTable: !!editedProduct?.dataTable,
            hideChangeParent: !!editedProduct?.hideChangeParent,
            linkedNodeDeleteStrategy:
                editedProduct?.linkedNodeDeleteStrategy ?? LinkedNodeDeleteStrategy.Ask,
            nodeReadonlyExpression: editedProduct?.nodeReadonly?.expression ?? '',
            nodeReadonlyExcludedFields: (editedProduct?.nodeReadonly?.excludedFields ?? []).join(
                ', ',
            ),
            nodeReadonlyMessage: editedProduct?.nodeReadonly?.message ?? '',
            assignmentPropagationDirection:
                editedProduct?.assignmentPropagation?.direction ??
                AssignmentPropagationDirection.None,
            assignmentPropagationMode:
                editedProduct?.assignmentPropagation?.mode ?? AssignmentPropagationMode.Auto,
            assignmentPropagationSchemaCodes: (
                editedProduct?.assignmentPropagation?.objectSchemaCodes ?? []
            ).join(', '),
            readFilters: toDrafts(editedProduct?.readFilters ?? []),
        }),
        [editedProduct],
    );

    return (
        <Grid container>
            <Grid item xs={12}>
                <FormElement
                    formKey={
                        mode === ModeType.EDIT_PRODUCT_INFO
                            ? `edit-product-info ${product.id}`
                            : 'new-product-info'
                    }
                    defaultValues={defaultValues}
                    submitHandler={onFormSubmit}
                    changeHandler={isDirty => setIsDirty(isDirty)}
                >
                    {({ control, setValue, formState, watch }) => (
                        // Inset of the EditNode title, shared by the title and the fields; one
                        // gap between every block, title and section dividers included.
                        <Stack gap={2} px={3} paddingBottom={4}>
                            <ProductActionBar>
                                {mode === ModeType.EDIT_PRODUCT_INFO && ( // delete-button only in edit-mode
                                    <>
                                        <HeaderBarIconButton
                                            label={intl.formatMessage({
                                                id: 'duplicate',
                                                defaultMessage: 'Duplicate',
                                            })}
                                            icon={<ContentCopyOutlinedIcon />}
                                            disabled={
                                                isUpdatingProductInfo ||
                                                isRemovingProductInfo ||
                                                isDuplicatingObjectSchema
                                            }
                                            onClick={() => {
                                                setSelectedProductInfo(product);
                                                setIsDuplicateDialogOpen(true);
                                            }}
                                        />
                                        <HeaderBarIconButton
                                            label={intl.formatMessage({
                                                id: 'delete',
                                                defaultMessage: 'Delete',
                                            })}
                                            icon={<DeleteOutlinedIcon />}
                                            onClick={deleteHandler}
                                            disabled={
                                                product.isReferenced ||
                                                isUpdatingProductInfo ||
                                                isDuplicatingObjectSchema ||
                                                isRemovingProductInfo
                                            }
                                        />
                                    </>
                                )}
                                <SubmitButton
                                    color="secondary"
                                    contrastColor="primary"
                                    message="Save"
                                    loading={isCreatingProductInfo || isUpdatingProductInfo}
                                    // Nothing to save until a field differs from its initial value.
                                    disabled={
                                        !Object.keys(formState.dirtyFields).length ||
                                        isDuplicatingObjectSchema ||
                                        isRemovingProductInfo
                                    }
                                />
                            </ProductActionBar>
                            <ProductFormTitle
                                {...(mode === ModeType.EDIT_PRODUCT_INFO
                                    ? { formatDefault: product.code, formatId: 'edit-product-info' }
                                    : {
                                          formatDefault: 'New product info',
                                          formatId: 'new-product-info',
                                      })}
                            />
                            <TextFieldElement
                                required
                                control={control}
                                id="code"
                                name="code"
                                label={intl.formatMessage({ defaultMessage: 'Code', id: 'code' })}
                            />
                            <Stack gap={2}>
                                <ControlFieldElement
                                    control={control}
                                    id="disableDeleteHierarchy"
                                    name="disableDeleteHierarchy"
                                    data-testid="disable-delete-hierarchy-switch"
                                    label={intl.formatMessage({
                                        defaultMessage: 'Disable delete hierarchy',
                                        id: 'disable-delete-hierarchy-field',
                                    })}
                                    onChange={(event, checked) => {
                                        if (!checked) {
                                            setValue('disableDelete', false);
                                        }
                                    }}
                                />
                                <ControlFieldElement
                                    control={control}
                                    id="disableDelete"
                                    name="disableDelete"
                                    data-testid="disable-delete-switch"
                                    label={intl.formatMessage({
                                        defaultMessage: 'Disable delete',
                                        id: 'disable-delete-field',
                                    })}
                                    onChange={(event, checked) => {
                                        if (checked) {
                                            setValue('disableDeleteHierarchy', true);
                                        }
                                    }}
                                />
                                <ControlFieldElement
                                    control={control}
                                    id="disableEditCode"
                                    name="disableEditCode"
                                    data-testid="disable-edit-code-switch"
                                    label={intl.formatMessage({
                                        defaultMessage: 'Disable edit code',
                                        id: 'disable-edit-code-field',
                                    })}
                                />
                                <ControlFieldElement
                                    control={control}
                                    id="hideChangeParent"
                                    name="hideChangeParent"
                                    data-testid="hide-change-parent-switch"
                                    label={intl.formatMessage({
                                        defaultMessage: 'Hide change parent',
                                        id: 'hide-change-parent-field',
                                    })}
                                />
                                <SelectFieldElement
                                    control={control}
                                    id="linkedNodeDeleteStrategy"
                                    name="linkedNodeDeleteStrategy"
                                    data-testid="linked-node-delete-strategy-select"
                                    labelId="linkedNodeDeleteStrategy"
                                    selectFieldTitle={{
                                        formatDefault: 'Nodi collegati alla cancellazione',
                                        inputLabelId: 'linkedNodeDeleteStrategy',
                                        formatId: 'linked-node-delete-strategy-field',
                                    }}
                                    fieldOptions={{
                                        valueLabel: 'code',
                                        nameLabel: 'label',
                                        options: Object.values(LinkedNodeDeleteStrategy).map(
                                            strategy => ({
                                                code: strategy,
                                                label: intl.formatMessage(
                                                    linkedNodeDeleteStrategyMessages[strategy],
                                                ),
                                            }),
                                        ),
                                    }}
                                />
                                <Stack flexDirection="row" alignItems="flex-end" gap={1}>
                                    <TextFieldElement
                                        control={control}
                                        id="hideDisableToggleDepths"
                                        name="hideDisableToggleDepths"
                                        disabled={!watch('hideDisableToggle')}
                                        label={intl.formatMessage({
                                            defaultMessage: 'Hide disable toggle at depths',
                                            id: 'hide-disable-toggle-depths-field',
                                        })}
                                        helperText={intl.formatMessage({
                                            defaultMessage:
                                                'Comma-separated node depths (root = 1). Empty = every depth.',
                                            id: 'hide-disable-toggle-depths-helper',
                                        })}
                                    />
                                    <InlineToggle>
                                        <ControlFieldElement
                                            control={control}
                                            id="hideDisableToggle"
                                            name="hideDisableToggle"
                                            data-testid="hide-disable-toggle-switch"
                                            label={intl.formatMessage({
                                                defaultMessage: 'Hide disable toggle',
                                                id: 'hide-disable-toggle-field',
                                            })}
                                        />
                                    </InlineToggle>
                                </Stack>
                                <Stack flexDirection="row" alignItems="flex-end" gap={1}>
                                    <SelectFieldElement
                                        control={control}
                                        id="listName"
                                        name="listName"
                                        data-testid="list-name-select"
                                        labelId="listName"
                                        selectFieldTitle={{
                                            formatDefault: 'List Name',
                                            inputLabelId: 'listName',
                                            formatId: 'listName',
                                        }}
                                        fieldOptions={{
                                            options: extractListNames(editedProduct),
                                        }}
                                    />
                                    <InlineToggle>
                                        <ControlFieldElement
                                            control={control}
                                            id="showListNameInEdit"
                                            name="showListNameInEdit"
                                            data-testid="show-list-name-in-edit-switch"
                                            label={intl.formatMessage({
                                                defaultMessage: 'Show List Name in edit',
                                                id: 'show-list-name-in-edit-field',
                                            })}
                                        />
                                    </InlineToggle>
                                </Stack>
                                <Stack flexDirection="row" alignItems="flex-end" gap={1}>
                                    <TextFieldElement
                                        control={control}
                                        id="codeCreationExpression"
                                        name="codeCreationExpression"
                                        label={intl.formatMessage({
                                            defaultMessage: 'Code creation expression',
                                            id: 'codeCreationExpression',
                                        })}
                                    />
                                    <InlineToggle>
                                        <ControlFieldElement
                                            control={control}
                                            id="automaticParentCreation"
                                            name="automaticParentCreation"
                                            label={intl.formatMessage({
                                                defaultMessage: 'Automatic parent creation',
                                                id: 'automaticParentCreation',
                                            })}
                                        />
                                    </InlineToggle>
                                </Stack>
                                <TextFieldElement
                                    control={control}
                                    id="codeLabel"
                                    name="codeLabel"
                                    label={intl.formatMessage({
                                        defaultMessage: 'Code label',
                                        id: 'codeLabel',
                                    })}
                                />
                                <SectionDivider
                                    label={intl.formatMessage({
                                        id: 'chip.publish-settings',
                                        defaultMessage: 'Publish settings',
                                    })}
                                />
                                <Stack flexDirection="row" alignItems="center" gap={1}>
                                    <ControlFieldElement
                                        control={control}
                                        id="enablePublish"
                                        name="enablePublish"
                                        data-testid="enable-publish-switch"
                                        label={intl.formatMessage({
                                            defaultMessage: 'Enable publish',
                                            id: 'enable-publish-field',
                                        })}
                                    />
                                    <ControlFieldElement
                                        control={control}
                                        id="bulkPublish"
                                        name="bulkPublish"
                                        data-testid="bulk-publish-switch"
                                        label={intl.formatMessage({
                                            defaultMessage: 'Bulk publish',
                                            id: 'bulk-publish-field',
                                        })}
                                    />
                                    <ControlFieldElement
                                        control={control}
                                        id="showPublishInfo"
                                        name="showPublishInfo"
                                        data-testid="show-publish-info-switch"
                                        label={intl.formatMessage({
                                            defaultMessage: 'Show publish info',
                                            id: 'show-publish-info-field',
                                        })}
                                    />
                                </Stack>
                                <SectionDivider
                                    label={intl.formatMessage({
                                        id: 'chip.assignment-settings',
                                        defaultMessage: 'Assignment settings',
                                    })}
                                />
                                <Stack flexDirection="row" alignItems="center" gap={1}>
                                    <ControlFieldElement
                                        control={control}
                                        id="enableOwner"
                                        name="enableOwner"
                                        data-testid="enable-owner-switch"
                                        label={intl.formatMessage({
                                            defaultMessage: 'Enable assignment',
                                            id: 'enable-owner-field',
                                        })}
                                    />
                                </Stack>
                                <SelectFieldElement
                                    control={control}
                                    id="assignmentPropagationDirection"
                                    name="assignmentPropagationDirection"
                                    data-testid="assignment-propagation-direction-select"
                                    labelId="assignmentPropagationDirection"
                                    selectFieldTitle={{
                                        formatDefault: "Propaga l'assegnazione ai nodi collegati",
                                        inputLabelId: 'assignmentPropagationDirection',
                                        formatId: 'assignment-propagation-direction-field',
                                    }}
                                    fieldOptions={{
                                        valueLabel: 'code',
                                        nameLabel: 'label',
                                        options: Object.values(AssignmentPropagationDirection).map(
                                            direction => ({
                                                code: direction,
                                                label: intl.formatMessage(
                                                    assignmentPropagationDirectionMessages[
                                                        direction
                                                    ],
                                                ),
                                            }),
                                        ),
                                    }}
                                />
                                <SelectFieldElement
                                    control={control}
                                    id="assignmentPropagationMode"
                                    name="assignmentPropagationMode"
                                    data-testid="assignment-propagation-mode-select"
                                    labelId="assignmentPropagationMode"
                                    disabled={
                                        watch('assignmentPropagationDirection') ===
                                        AssignmentPropagationDirection.None
                                    }
                                    selectFieldTitle={{
                                        formatDefault: 'Modalità di propagazione',
                                        inputLabelId: 'assignmentPropagationMode',
                                        formatId: 'assignment-propagation-mode-field',
                                    }}
                                    fieldOptions={{
                                        valueLabel: 'code',
                                        nameLabel: 'label',
                                        options: Object.values(AssignmentPropagationMode).map(
                                            propagationMode => ({
                                                code: propagationMode,
                                                label: intl.formatMessage(
                                                    assignmentPropagationModeMessages[
                                                        propagationMode
                                                    ],
                                                ),
                                            }),
                                        ),
                                    }}
                                />
                                <TextFieldElement
                                    control={control}
                                    id="assignmentPropagationSchemaCodes"
                                    name="assignmentPropagationSchemaCodes"
                                    disabled={
                                        watch('assignmentPropagationDirection') ===
                                        AssignmentPropagationDirection.None
                                    }
                                    label={intl.formatMessage({
                                        defaultMessage: 'Object schema dei nodi collegati',
                                        id: 'assignment-propagation-schema-codes-field',
                                    })}
                                    helperText={intl.formatMessage({
                                        defaultMessage:
                                            'Codici object schema separati da virgola. Vuoto = tutti.',
                                        id: 'assignment-propagation-schema-codes-helper',
                                    })}
                                />
                                <SectionDivider
                                    label={intl.formatMessage({
                                        id: 'chip.data-table-settings',
                                        defaultMessage: 'Data table settings',
                                    })}
                                />
                                <Stack flexDirection="row" alignItems="center" gap={1}>
                                    <ControlFieldElement
                                        control={control}
                                        id="dataTable"
                                        name="dataTable"
                                        data-testid="data-table-switch"
                                        label={intl.formatMessage({
                                            defaultMessage: 'Edit as data table',
                                            id: 'data-table-field',
                                        })}
                                    />
                                </Stack>
                                <SectionDivider
                                    label={intl.formatMessage({
                                        id: 'chip.node-readonly-settings',
                                        defaultMessage: 'Node readonly rule',
                                    })}
                                />
                                <TextFieldElement
                                    control={control}
                                    id="nodeReadonlyExpression"
                                    name="nodeReadonlyExpression"
                                    multiLine
                                    label={intl.formatMessage({
                                        defaultMessage: 'Readonly expression',
                                        id: 'node-readonly-expression-field',
                                    })}
                                    helperText={intl.formatMessage({
                                        defaultMessage:
                                            'JSONata evaluated against the saved node, same context as a default value expression. The node is locked only when it returns true. Empty = no rule.',
                                        id: 'node-readonly-expression-helper',
                                    })}
                                />
                                <TextFieldElement
                                    control={control}
                                    id="nodeReadonlyExcludedFields"
                                    name="nodeReadonlyExcludedFields"
                                    disabled={!watch('nodeReadonlyExpression').trim()}
                                    label={intl.formatMessage({
                                        defaultMessage: 'Fields editable when locked',
                                        id: 'node-readonly-excluded-fields-field',
                                    })}
                                    helperText={intl.formatMessage({
                                        defaultMessage:
                                            'Comma-separated "groupCode.fieldCode" entries. Include the field the rule depends on so the node can be unlocked.',
                                        id: 'node-readonly-excluded-fields-helper',
                                    })}
                                />
                                <TextFieldElement
                                    control={control}
                                    id="nodeReadonlyMessage"
                                    name="nodeReadonlyMessage"
                                    disabled={!watch('nodeReadonlyExpression').trim()}
                                    label={intl.formatMessage({
                                        defaultMessage: 'Readonly message',
                                        id: 'node-readonly-message-field',
                                    })}
                                    helperText={intl.formatMessage({
                                        defaultMessage:
                                            'Shown in the banner, in the field tooltips and in the error returned on a rejected save.',
                                        id: 'node-readonly-message-helper',
                                    })}
                                />
                                {editedProduct && readFiltersAvailable && (
                                    <ReadFiltersFields objectSchemaId={editedProduct.id} />
                                )}
                            </Stack>
                        </Stack>
                    )}
                </FormElement>
            </Grid>

            {/* Confirm dialog for removing a productInfo */}
            <ConfirmDialog
                isOpen={deleteDialogOpen}
                title={intl.formatMessage({
                    defaultMessage: 'Delete item',
                    id: 'delete-item',
                })}
                handleClose={handleDeleteClose}
            >
                <FormattedMessage
                    id="confirm-delete-product-info"
                    defaultMessage="Are you sure you want to delete this element?"
                />
            </ConfirmDialog>

            {/* Confirm dialog for duplicate a productInfo */}
            <Dialog
                open={isDuplicateDialogOpen}
                onClose={() => {
                    setIsDuplicateDialogOpen(false);
                    setNewProductInfoCode(undefined);
                }}
            >
                <DialogTitle>
                    <FormattedMessage
                        id="duplicate-product-title"
                        defaultMessage="Duplicate Product"
                    />
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        <FormattedMessage
                            id="duplicate-product-description"
                            defaultMessage="Enter the Code for the new Product"
                        />
                    </DialogContentText>
                    <TextField
                        fullWidth
                        id="code"
                        label={intl.formatMessage({ defaultMessage: 'Code', id: 'code' })}
                        type="text"
                        autoFocus
                        required
                        value={newProductInfoCode}
                        onChange={e => {
                            setNewProductInfoCode(e.target.value);
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setIsDuplicateDialogOpen(false)}
                        color="secondary"
                        autoFocus
                        startIcon={<CancelIcon />}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={async () => {
                            if (newProductInfoCode?.length && selectedProductInfo?.id) {
                                setNewProductInfoCode(undefined);
                                setIsDuplicateDialogOpen(false);
                                await duplicateObjectSchema({
                                    variables: {
                                        aggregateId: selectedProductInfo.id,
                                        code: newProductInfoCode,
                                    },
                                });
                                await refetchProducts?.();
                                SnackbarUtils.success(
                                    `Product "${newProductInfoCode}" duplicated from product "${selectedProductInfo.code}".`,
                                );
                            }
                        }}
                        disabled={!newProductInfoCode?.length}
                        color="primary"
                        startIcon={<ContentCopyIcon />}
                    >
                        Ok
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
};
